import { format, isToday, isYesterday } from "date-fns";
import LogManager from "./LogManager";

/**
 * Fetches summaries from the backend between now and 10 days ago.
 * Falls back to empty list on failure.
 *
 * @returns {Promise<{ groups: Object}>}
 */
export const fetchSummaries = async () => {
  const startTimeEpochSec = Math.floor(Date.now() / 1000);
  const tenDaysAgoEpochSec = startTimeEpochSec - 10 * 24 * 60 * 60;

  const request = {
    queryStartTime: tenDaysAgoEpochSec,
    queryEndTime: startTimeEpochSec,
    count: "50",
  };

  LogManager.info(
    "Calling /summaries. Request body is: " + JSON.stringify(request)
  );

  try {
    const response = await fetch("/summaries", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
    });

    const responseJson = await response.json();
    LogManager.info("The responseJson of /summaries is: ", responseJson);

    const summaryListString = JSON.parse(responseJson.Payload);
    const summaryListJson = JSON.parse(summaryListString);

    LogManager.info("The summaryListJson is:", summaryListJson);
    return summaryListJson;
  } catch (error) {
    LogManager.error("Failed to fetch summaries:", error);
    throw error;
  }
};

/**
 * Preprocesses the summary list by grouping summaries into
 * Today, Yesterday, or a formatted date string (e.g., Friday, October 4),
 * and returns them in descending date order (most recent first).
 *
 * Used by Summaries tab in meeting
 * @param {Array} summaryListJson - List of summaries
 * @returns {Object} - Ordered { [groupLabel]: summary[] }
 */
export const generateSummaryGroup = (summaryListJson) => {
  const summaryGroups = {};

  summaryListJson.forEach((summary) => {
    if (!summary.meetingStartTime) {
      console.warn("Missing meetingStartTime in summary:", summary);
      return;
    }

    const date = new Date(summary.meetingStartTime * 1000);
    let groupKey;

    if (isToday(date)) {
      groupKey = "Today";
    } else if (isYesterday(date)) {
      groupKey = "Yesterday";
    } else {
      groupKey = format(date, "EEEE, MMMM d"); // e.g., "Friday, October 4"
    }

    if (!summaryGroups[groupKey]) {
      summaryGroups[groupKey] = [];
    }

    summaryGroups[groupKey].push(summary);
  });

  // Sort summaries within each group by descending meetingStartTime
  Object.values(summaryGroups).forEach((list) =>
    list.sort((a, b) => b.meetingStartTime - a.meetingStartTime)
  );

  // Compute latest meetingStartTime for each group to sort the keys
  const groupEntries = Object.entries(summaryGroups).map(([key, list]) => {
    const latestTime = Math.max(...list.map((s) => s.meetingStartTime));
    return [key, list, latestTime];
  });

  // Sort groups by latest meeting time (descending)
  groupEntries.sort((a, b) => b[2] - a[2]);

  // Rebuild the sorted group object
  const sortedGroups = {};
  groupEntries.forEach(([key, list]) => {
    sortedGroups[key] = list;
  });

  return sortedGroups;
};

/**
 * Paginates the grouped summaries into pages of 4 summaries each,
 * preserving the group title with each chunk.
 *
 * Used by Summaries tab in client
 *
 * @param {Object} groups - Grouped summaries, e.g. { "Today": [..], "Yesterday": [..] }
 * @returns {Array<{ groupTitle: string, summaries: Array }>}
 */
export const preProcessInClient = (groups) => {
  const newPages = [];

  Object.entries(groups).forEach(([groupTitle, summaries]) => {
    for (let i = 0; i < summaries.length; i += 4) {
      newPages.push({
        groupTitle,
        summaries: summaries.slice(i, i + 4),
      });
    }
  });

  return newPages;
};

/**
 * Truncates attendee names to fit within 180px width.
 * @param attendeeString - Comma-separated attendee names (e.g., "Tong Zhang,Tianlei Xu,Lu Wang,Feng Shan")
 * @returns A string trimmed with "..." if it exceeds 180px width
 */
export const truncateAttendees = (attendeeString) => {
  const maxWidth = 170;

  // Create an offscreen measurement element
  const span = document.createElement("span");
  span.style.fontSize = "12px";
  span.style.fontStyle = "normal";
  span.style.fontWeight = "400";
  span.style.lineHeight = "16px";
  span.style.position = "absolute";
  span.style.visibility = "hidden";
  span.style.whiteSpace = "nowrap";
  span.style.pointerEvents = "none";
  span.style.left = "-9999px";
  document.body.appendChild(span);

  const names = attendeeString.split(",").filter(Boolean);
  let result = "";

  for (let i = 0; i < names.length; i++) {
    const candidate = result ? `${result},${names[i]}` : names[i];
    span.textContent = candidate;
    if (span.offsetWidth > maxWidth) {
      result += "...";
      break;
    } else {
      result = candidate;
    }
  }

  document.body.removeChild(span);
  return result;
}

